import { useDraggable } from '@dnd-kit/core'
import { formatDistanceToNow } from 'date-fns'
import { FileText } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { type KnowledgeDocumentListItem } from '@/models/knowledgeDocument'

export const DOCUMENT_DRAG_PREFIX = 'doc:'

export function DocumentRowContent({
  doc,
  selected = false,
}: {
  doc: KnowledgeDocumentListItem
  selected?: boolean
}) {
  const updatedAt = doc.currentVersionUpdatedAt ? new Date(doc.currentVersionUpdatedAt) : null
  const relative = updatedAt ? formatDistanceToNow(updatedAt, { addSuffix: true }) : 'never'
  const email = doc.currentVersionUpdatedByEmail ?? '—'

  return (
    <div
      className={cn(
        'bg-surface w-full rounded-md border px-3 py-2 text-left',
        selected ? 'border-blue-500 bg-blue-50' : 'border-transparent'
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2">
          <FileText className="text-muted-foreground size-4 shrink-0" />
          <span className="truncate font-medium">{doc.title}</span>
        </span>
        <div className="flex shrink-0 items-center gap-1">
          {doc.isPrivate && (
            <Badge variant="secondary" className="text-xs">
              Private
            </Badge>
          )}
          {!doc.isEnabled && (
            <Badge variant="outline" className="text-xs">
              Disabled
            </Badge>
          )}
        </div>
      </div>
      <div className="text-muted-foreground pl-6 text-xs">
        Updated {relative} by {email}
      </div>
    </div>
  )
}

type Props = {
  doc: KnowledgeDocumentListItem
  depth: number
  selected: boolean
  onSelect: () => void
}

export function DocumentRow({ doc, depth, selected, onSelect }: Props) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${DOCUMENT_DRAG_PREFIX}${doc.slug}`,
    data: { type: 'document', doc },
  })

  return (
    <div
      ref={setNodeRef}
      style={{ paddingLeft: depth * 16 }}
      className={cn('touch-none transition-opacity', isDragging && 'opacity-40')}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        onClick={() => !isDragging && onSelect()}
        className="w-full cursor-grab rounded-md text-left hover:bg-gray-50 active:cursor-grabbing"
      >
        <DocumentRowContent doc={doc} selected={selected} />
      </button>
    </div>
  )
}
